import React, {useMemo} from 'react';

import {EEventSource} from 'coreClasses/events/IEvents';
import {ESearchProvider} from 'coreClasses/search/ISearch';
import {useLocation, useProducts, useSearchMgrInstance} from 'coreHooks';
import {useCategory} from 'coreHooks/categories';
import useChangeEffect from 'coreHooks/useChangeEffect';
import {Link} from 'coreUtils/adapters/router';

import {VisibilityContextProvider} from 'brand/app/contexts/viewportProductContext';
import {ProductsGrid} from 'brandComponents/ProductsGrid';
import Typography from 'brandComponents/Typography';
import {FIRST_PAGE} from 'brandConstants';
import useViewItemListEvent from 'brandHooks/useViewItemListEvent';

import {ICategoryIncludeProps} from './ICategoryInclude';

import styles from './CategoryInclude.module.scss';

const CategoryInclude = ({category, refinements, sort, productsQuantity}: ICategoryIncludeProps) => {
	const categoryItem = category?.items && category.items.length > 0 && category.items[0];
	const searchMgrInstance = useSearchMgrInstance({
		key: categoryItem.id,
		compare: ['query', 'pages'],
		provider: ESearchProvider.SFCC,
	});
	const searchModel = searchMgrInstance.getStoredSearchModel();
	const productHitIds = searchModel.getAllHits().map((productHit) => productHit.getProductId());
	const products = useProducts(productHitIds, true);
	const location = useLocation();
	const isHomePage = location?.currentRoute?.includes('Content-Show');
	const categoryModel = useCategory(categoryItem.id);

	const {handleProductVisibility} = useViewItemListEvent(
		products,
		isHomePage ? EEventSource.HOME : EEventSource.CONTENTFUL_CATEGORY,
		categoryItem.id,
	);

	// Once per mount: not again when a page kept alive (`<Activity>`) is shown again.
	useChangeEffect(() => {
		searchMgrInstance.search({
			refinements: {...refinements, cgid: [categoryItem.id]},
			sortingRule: sort?.length > 0 ? sort[0] : '',
			page: FIRST_PAGE,
			limitParam: productsQuantity,
			eventSource: EEventSource.CONTENTFUL_CATEGORY,
		});
	}, [], true);

	const visibilityContextValue = useMemo(
		() => ({
			handleVisibilityChange: handleProductVisibility,
		}),
		[handleProductVisibility],
	);

	return (
		<div className={styles['category-include_products']}>
			<Typography variant="h2" classNames={styles['category-include_products-title']}>
				<Link to={categoryModel?.getUrl()}>{categoryModel?.getName()}</Link>
			</Typography>
			<VisibilityContextProvider value={visibilityContextValue}>
				<ProductsGrid>
					{searchModel?.getPages().map((pageModel) => (
						<ProductsGrid.SearchHits
							key={pageModel.getPageNumber()}
							searchHits={pageModel.getHits()}
							page={pageModel.getPageNumber()}
							pageSize={searchModel.getPageSize()}
							productTitleTag="span"
							lowestPage={searchModel.getLowestLoadedPageNumber()}
						/>
					))}
				</ProductsGrid>
			</VisibilityContextProvider>
		</div>
	);
};

export default CategoryInclude;
